import {array_move, array_splice, generateRandomKey, is_same_object} from "../../utils/functions";
import {ImageKeyBlank} from "../../assets/BehaviorImage";
import {CellContentInterface} from "./CellContentInterface";
import {TextStyle} from "./TextStyle";
import {CHANGE_TEXT_STYLE_TYPE, TOGGLE_TEXT_STYLE_TYPE} from "./TextStyleType";
import {JcEditorState, RawJcEditorState} from "./JcEditorState";

export type RawBehaviorLines = {
    keys: string[];
    lines: RawJcEditorState[];
    images: string[];
}

export class BehaviorLines implements CellContentInterface {

    private readonly keys: string[];
    private readonly lines: JcEditorState[];
    private readonly images: string[];

    constructor(keys: string[], lines: JcEditorState[], images: string[]) {
        if (keys.length !== lines.length || keys.length !== images.length || lines.length !== images.length) {
            throw new Error("invalid length");
        }

        this.keys = keys;
        this.lines = lines;
        this.images = images;
    }

    static fromRaw(raw: RawBehaviorLines) {
        return new BehaviorLines(
            raw.keys,
            raw.lines.map(raw => JcEditorState.createFromRaw(raw)),
            raw.images
        );
    }

    static fromStr(str: string) {
        return new BehaviorLines(
            [generateRandomKey()],
            [BehaviorLines.makeDefaultEditorState(str)],
            [ImageKeyBlank]
        );
    }

    private static makeDefaultEditorState(str: string) {
        return JcEditorState.createEmptyCentering();
    }

    toRaw(): RawBehaviorLines {
        return {
            keys: Array.from(this.keys),
            lines: Array.from(this.lines.map(editor_state => editor_state.toJSON())),
            images: Array.from(this.images)
        };
    }

    move(fromKey: string, toKey: string) {

        const from = this.keys.indexOf(fromKey);
        const to = this.keys.indexOf(toKey);
        if (from === -1 || to === -1) {
            throw new Error("invalid key?");
        }

        return new BehaviorLines(
            array_move(this.keys, from, to),
            array_move(this.lines, from, to),
            array_move(this.images, from, to)
        );
    }

    remove(key: string) {
        const index = this.keys.indexOf(key);
        if (index === -1) {
            throw new Error("invalid key?");
        }

        if (this.keys.length === 1) {
            return new BehaviorLines(
                Array.from(this.keys),
                [BehaviorLines.makeDefaultEditorState('')],
                [ImageKeyBlank]
            );
        }

        return new BehaviorLines(
            array_splice(this.keys, index, 1),
            array_splice(this.lines, index, 1),
            array_splice(this.images, index, 1)
        );
    }

    append() {
        return new BehaviorLines(
            this.keys.concat([generateRandomKey()]),
            this.lines.concat([BehaviorLines.makeDefaultEditorState('')]),
            this.images.concat([ImageKeyBlank])
        );
    }

    setImage(key: string, image: string) {
        const index = this.keys.indexOf(key);
        if (index === -1) {
            throw new Error("invalid key?");
        }

        const new_images = Array.from(this.images);
        new_images[index] = image;

        return new BehaviorLines(
            Array.from(this.keys),
            Array.from(this.lines),
            new_images
        );
    }

    setContent(key: string, editor_state: JcEditorState) {
        const index = this.keys.indexOf(key);
        if (index === -1) {
            throw new Error("invalid key?");
        }

        const new_lines = Array.from(this.lines);
        new_lines[index] = editor_state;

        return new BehaviorLines(
            Array.from(this.keys),
            new_lines,
            Array.from(this.images),
        );
    }

    convertContents(converter: (raw: JcEditorState) => JcEditorState) {

        return new BehaviorLines(
            Array.from(this.keys),
            this.lines.map(e => converter(e)),
            Array.from(this.images),
        );
    }

    get Keys() {
        return Array.from(this.keys);
    }

    toArray() {
        return this.keys.map(
            (key, i) =>
                0 || {key: key, image: this.images[i], content: this.lines[i]}
        )
    }

    get IsEmpty(): boolean {
        return false;
    }

    toJSON(): object {
        return this.toRaw();
    }

    toText(): string {
        return this.lines.map(editor_state => editor_state.toText()).join("\n----\n");
    }

    equals(other: this): boolean {
        return is_same_object(this.toJSON(), other.toJSON());
    }

    getTextStyle(): TextStyle {
        const styles = this.lines.map(line => line.getTextStyle());
        if (styles.length === 0) {
            return TextStyle.createDefault();
        }
        return styles.reduce((prev, current) => prev.and(current));
    }

    changeTextStyle(style_type: CHANGE_TEXT_STYLE_TYPE, value: string): BehaviorLines {
        return this.convertContents(
            (editor_state) => editor_state.changeTextStyle(style_type, value)
        )
    }

    toggleTextStyle(style_type: TOGGLE_TEXT_STYLE_TYPE): BehaviorLines {
        return this.convertContents(
            (editor_state) => editor_state.toggleTextStyle(style_type)
        )
    }

    static fromJSON(object: any): BehaviorLines {
        return BehaviorLines.fromRaw(object);
    }

}
